import React, {useEffect, useMemo, useRef} from 'react';
import echarts from '@/utils/echartsCore';
import i18n from 'i18n';

export interface AuditEventSankeyNode {
    name: string;
}

export interface AuditEventSankeyLink {
    source: string;
    target: string;
    value: number;
}

export interface UserAuditAuditEventSankeyProps {
    nodes: AuditEventSankeyNode[];
    links: AuditEventSankeyLink[];
}

const CHART_MIN_HEIGHT_PX = 420;
const LABEL_MAX_LEN = 26;

function shortNodeLabel(name: string): string {
    const t = (name ?? '').trim();
    if (!t) {
        return '—';
    }
    return t.length > LABEL_MAX_LEN ? `${t.slice(0, LABEL_MAX_LEN)}…` : t;
}

function mergeLinksByPair(links: AuditEventSankeyLink[]): AuditEventSankeyLink[] {
    const totals = new Map<string, AuditEventSankeyLink>();
    for (const l of links) {
        const source = String(l.source ?? '');
        const target = String(l.target ?? '');
        const sep = '\u0000';
        const key = `${source}${sep}${target}`;
        const add = Number(l.value) || 0;
        const prev = totals.get(key);
        if (prev) {
            prev.value += add;
        } else {
            totals.set(key, {source, target, value: add});
        }
    }
    return [...totals.values()];
}

function greedyAcyclicSankeyLinks(links: AuditEventSankeyLink[]): AuditEventSankeyLink[] {
    const sorted = [...links].sort((a, b) => b.value - a.value);
    const adj = new Map<string, string[]>();
    const result: AuditEventSankeyLink[] = [];

    const reachabilityFrom = (start: string, goal: string): boolean => {
        if (start === goal) {
            return true;
        }
        const visited = new Set<string>();
        const stack = [start];
        while (stack.length > 0) {
            const u = stack.pop()!;
            if (u === goal) {
                return true;
            }
            if (visited.has(u)) {
                continue;
            }
            visited.add(u);
            const outs = adj.get(u);
            if (!outs) {
                continue;
            }
            for (const v of outs) {
                stack.push(v);
            }
        }
        return false;
    };

    for (const link of sorted) {
        const source = String(link.source ?? '');
        const target = String(link.target ?? '');
        if (source === target) {
            continue;
        }
        if (reachabilityFrom(target, source)) {
            continue;
        }
        result.push({source, target, value: link.value});
        if (!adj.has(source)) {
            adj.set(source, []);
        }
        adj.get(source)!.push(target);
    }
    return result;
}

function nodesFromLinks(linkList: AuditEventSankeyLink[]): AuditEventSankeyNode[] {
    const names = new Set<string>();
    for (const l of linkList) {
        names.add(l.source);
        names.add(l.target);
    }
    return [...names].sort().map((name) => ({name}));
}

const UserAuditAuditEventSankey: React.FC<UserAuditAuditEventSankeyProps> = ({nodes: _nodes, links}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const {displayNodes, displayLinks} = useMemo(() => {
        const merged = mergeLinksByPair(links);
        const dagLinks = greedyAcyclicSankeyLinks(merged);
        return {
            displayNodes: nodesFromLinks(dagLinks),
            displayLinks: dagLinks,
        };
    }, [links]);
    const showChart = displayLinks.length > 0;

    useEffect(() => {
        const el = chartRef.current;
        if (!el || !showChart) {
            return;
        }
        const chart = echarts.init(el);
        const handleResize = () => {
            if (!chart.isDisposed()) {
                chart.resize();
            }
        };
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(el);
        }
        window.addEventListener('resize', handleResize);

        chart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 11},
                tooltip: {
                    trigger: 'item',
                    triggerOn: 'mousemove',
                },
                series: [
                    {
                        type: 'sankey' as const,
                        emphasis: {focus: 'adjacency' as const},
                        nodeAlign: 'justify' as const,
                        nodeWidth: 18,
                        nodeGap: 10,
                        layoutIterations: 48,
                        data: displayNodes.map((n) => ({name: n.name})),
                        links: displayLinks.map((l) => ({
                            source: l.source,
                            target: l.target,
                            value: l.value,
                        })),
                        lineStyle: {
                            color: 'gradient',
                            curveness: 0.5,
                            opacity: 0.35,
                        },
                        label: {
                            fontSize: 11,
                            color: '#334155',
                            formatter: (p: {name?: string}) => shortNodeLabel(String(p.name ?? '')),
                        },
                        itemStyle: {
                            borderWidth: 0,
                        },
                    },
                ],
            },
            true,
        );

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            chart.dispose();
        };
    }, [displayNodes, displayLinks, showChart]);

    if (!showChart) {
        return <p className="text-muted small mb-0">{i18n.t('ub_flows_sankey_empty.text')}</p>;
    }

    return (
        <div
            ref={chartRef}
            style={{
                width: '100%',
                minHeight: CHART_MIN_HEIGHT_PX,
                height: Math.max(
                    CHART_MIN_HEIGHT_PX,
                    Math.min(720, displayNodes.length * 28 + displayLinks.length * 4),
                ),
            }}
        />
    );
};

export default UserAuditAuditEventSankey;
